import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { navigationGuarded } from "./navigation-guard";
import { ROUTE_IDS } from "./routeTable";
import type { UIEvent } from "../lib/events-adapter";

/**
 * Shell router — URL-synced SPA router (History API). The browser URL is the
 * single source of truth: boot derives the route from window.location, every
 * nav writes the URL (pushState/replaceState), and popstate (back/forward,
 * deep-link, refresh) maps the URL back to a route id.
 *
 *   const nav = useNav();
 *   nav.go(route, params?)   — push a route   (history.pushState)
 *   nav.back()               — browser back   (history.back)
 *   nav.replace(route, p?)   — swap the top    (history.replaceState)
 *   nav.route / nav.params   — current route id + params
 *
 * routeId ↔ path is a 1:1 dots→slashes map: `hub.create.page ↔ /hub/create/page`,
 * `dashboard ↔ /dashboard`. Params serialize to the query string. The app is
 * served at the tenant root, so paths are absolute root-relative.
 *
 * Attend-host exception (2026-07-14 docket): on `attend.<tenant>` a BARE-slug
 * path — exactly what publicUrl()/openPublicEventPage() emit — resolves to
 * `event.public` with { slug }, and routeToPath emits the same bare-slug form
 * back so the public WYSIWYG URL survives boot-normalize and in-app navs.
 * `/event/public?slug=…` keeps working everywhere, unchanged.
 *
 * CD-owned shell code. Screens are resolved by routeTable.ts + <AppRouter/>.
 * All history.* calls are wrapped in try/catch so a sandboxed/opaque-origin
 * preview (where pushState throws) degrades gracefully to in-memory routing.
 */
export interface NavEntry {
  route: string;
  params: Record<string, unknown>;
}

export interface NavApi {
  route: string;
  params: Record<string, unknown>;
  canBack: boolean;
  /** Alias of canBack (prototype name). */
  canGoBack: boolean;
  /** The route one level down the stack, or null. */
  prevRoute: string | null;
  go: (route: string, params?: Record<string, unknown>) => void;
  back: () => void;
  replace: (route: string, params?: Record<string, unknown>) => void;
  /** Replace the entire stack (e.g. sign-out → signin). */
  reset: (route: string, params?: Record<string, unknown>) => void;
  /** Open the event detail drawer over the current screen. */
  openEvent: (ev: UIEvent) => void;
  /** Close the event detail drawer. */
  closeEvent: () => void;
  /** The event whose detail drawer is open, or null. */
  drawerEvent: UIEvent | null;
}

const NavContext = createContext<NavApi | null>(null);

export function useNav(): NavApi {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error("useNav() must be used inside <NavProvider>");
  return ctx;
}

const ROUTE_SET: ReadonlySet<string> = new Set(ROUTE_IDS as readonly string[]);

/** True for a route id the app declares. An address that resolves to anything
 *  else is a page that does not exist (see `parseLoc`). */
export function isKnownRoute(route: string): boolean {
  return ROUTE_SET.has(route);
}

// ── Anonymous attend host (2026-07-14 docket) ───────────────────────────────
// attend.<tenant> serves the published event landing/registration experience
// to ANONYMOUS visitors; studio.<tenant> stays SSO-walled. Same SPA build —
// the host is the switch.
/** Route ids an anonymous attend-host visitor may reach. Matches the gateway's
 *  anonymous allowlist (published-landing read + registration submit); extend
 *  only when the corresponding anonymous data path is live. */
export const PUBLIC_ATTEND_ROUTES: ReadonlySet<string> = new Set(["event.public", "event.register"]);

/** True when the app is served from the public attend host. Host-based per the
 *  docket (`attend.` subdomain / bare `attend` label). `__ATTEND_HOST__` is a
 *  window override so the CD preview harness (which can't fake a hostname) can
 *  exercise the anonymous flow. */ // ui-config
export function isAttendHost(): boolean {
  if (typeof window === "undefined") return false;
  if ((window as unknown as { __ATTEND_HOST__?: boolean }).__ATTEND_HOST__ === true) return true;
  const h = (window.location.hostname || "").toLowerCase();
  return h === "attend" || h.startsWith("attend.");
}

/** Anonymous-OK: a public event route requested on the attend host. Studio-host
 *  behavior (and every operator route on any host) is untouched. */
export function isPublicAttendRoute(route: string): boolean {
  return isAttendHost() && PUBLIC_ATTEND_ROUTES.has(route);
}

/** Every PUBLIC-facing host the composer serves this same SPA on — the full set
 *  from the tenant manifest's public hostname fields (event→attend,
 *  appointment→book, queue→join, staff→meet, landing→public), not just attend.
 *
 *  `isAttendHost()` above matches ONLY `attend`, which is correct for the
 *  anonymous event routes it gates. This is the broader question "is this a
 *  public visitor host at all?", used to guarantee that an operator surface —
 *  the sign-in/persona chooser included — can never render on any of them.
 *  Honors the same `__ATTEND_HOST__` preview override so the CD harness, which
 *  cannot fake a hostname, still exercises the public path. */ // ui-config
const PUBLIC_HOST_LABELS: ReadonlySet<string> = new Set(["attend", "book", "join", "meet", "public"]);
export function isPublicHost(): boolean {
  if (typeof window === "undefined") return false;
  if ((window as unknown as { __ATTEND_HOST__?: boolean }).__ATTEND_HOST__ === true) return true;
  const h = (window.location.hostname || "").toLowerCase();
  const label = h.split(".")[0] ?? "";
  return PUBLIC_HOST_LABELS.has(label);
}

/** Bare-slug path segment (what slugify() emits): single segment, no slashes. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/i;

/** Customer ids as they appear in a path segment. Deliberately permissive
 *  (uuid, prefixed, opaque) and deliberately NOT matching a slash — the id is
 *  one segment. */
const CUSTOMER_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._~-]*$/;

/** routeId + params → root-relative path (+ query). `landing.pages` → `/landing/pages`. */
export function routeToPath(route: string, params: Record<string, unknown> = {}): string {
  // Attend host: event.public keeps the WYSIWYG bare-slug URL (attend.<tenant>/<slug>)
  // instead of /event/public?slug=… — boot-normalize and in-app navs must not
  // rewrite the link the visitor arrived on.
  if (route === "event.public" && typeof params.slug === "string" && SLUG_RE.test(params.slug) && isAttendHost()) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (k === "slug" || v === null || v === undefined || v === "") continue;
      qs.set(k, String(v));
    }
    const q = qs.toString();
    return q ? `/${params.slug}?${q}` : `/${params.slug}`;
  }
  // Customer detail is the one PATH-PARAM route in the app: the drop requires
  // `/customers/<customerId>` to survive direct navigation and back/forward,
  // and a query param would not be that URL. Kept as a named exception rather
  // than a general pattern language, so no other route silently acquires one.
  if (route === "customers.detail" && typeof params.customerId === "string" && CUSTOMER_ID_RE.test(params.customerId)) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (k === "customerId" || v === null || v === undefined || v === "") continue;
      qs.set(k, String(v));
    }
    const q = qs.toString();
    return q ? `/customers/${encodeURIComponent(params.customerId)}?${q}` : `/customers/${encodeURIComponent(params.customerId)}`;
  }
  const path = "/" + route.split(".").join("/");
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    qs.set(k, String(v));
  }
  const q = qs.toString();
  return q ? `${path}?${q}` : path;
}

/** pathname (+ search) → routeId + params. `/landing/pages` → `landing.pages`. */
// #8 (2026-07-14 docket) — index redirects: a bare section path canonicalizes to
// its default sub-view instead of falling through to the dashboard fallback
// (typed/bookmarked `/calendar` deep-links bounced to /dashboard; the nav's own
// links already target /calendar/week).
const ROUTE_INDEX_REDIRECTS: Record<string, string> = { calendar: "calendar.week", workforce: "workforce.schedule", storeops: "storeops.floor" };
function pathToEntry(pathname: string, search: string): NavEntry {
  const clean = pathname.replace(/^\/+|\/+$/g, "");
  const raw = clean ? clean.split("/").join(".") : "dashboard";
  const route = ROUTE_INDEX_REDIRECTS[raw] || raw;
  const params: Record<string, unknown> = {};
  new URLSearchParams(search).forEach((v, k) => { params[k] = v; });
  const segments = clean.split("/");
  // `/customers/<customerId>` → customers.detail. Checked BEFORE the route-set
  // lookup would give up, and after it for `/customers` itself, which is a
  // known route id and resolves normally.
  if (!ROUTE_SET.has(route)) {
    if (segments.length === 2 && segments[0] === "customers" && CUSTOMER_ID_RE.test(segments[1])) {
      return { route: "customers.detail", params: { ...params, customerId: decodeURIComponent(segments[1]) } };
    }
  }
  // Attend host: an unknown single-segment path is a public-event slug —
  // attend.<tenant>/<slug> → event.public { slug } → <PublicEventLanding/> →
  // getPublishedLandingSurface(slug). Known route ids win (so /event/public
  // resolution is unchanged, and operator deep-links behave identically).
  if (!ROUTE_SET.has(route) && isAttendHost() && SLUG_RE.test(clean)) {
    return { route: "event.public", params: { ...params, slug: clean } };
  }
  return { route, params };
}

export function NavProvider({ initialRoute = "dashboard", initialParams = {}, children }: {
  initialRoute?: string;
  initialParams?: Record<string, unknown>;
  children: ReactNode;
}) {
  const parseLoc = useCallback((): NavEntry => {
    if (typeof window === "undefined") return { route: initialRoute, params: initialParams };
    const entry = pathToEntry(window.location.pathname, window.location.search);
    if (ROUTE_SET.has(entry.route)) return entry;
    // A document path (the preview harness is served at "/index.html") is not
    // a route anyone typed → boot fallback.
    if (/\.html?$/i.test(window.location.pathname)) return { route: initialRoute, params: initialParams };
    /* Any OTHER unknown path is kept as-is, so <AppRouter/> can say "this page
     * doesn't exist" at the address the user actually opened (2026-10-02). It
     * used to fall back to the dashboard silently: a mistyped or stale link like
     * /workforce/myschedule/2026-12-01 landed a Team Member on a Dashboard of
     * permission failures with the URL rewritten, so nothing said the link was
     * wrong. `isKnownRoute` lets the router tell the two apart. */
    return entry;
  }, [initialRoute, initialParams]);

  const stackRef = useRef<NavEntry[]>([]);
  const idxRef = useRef(0);
  const restoringPop = useRef(false), approvedPop = useRef(false);
  const resumePop = useRef<(() => void) | null>(null);

  const [current, setCurrent] = useState<NavEntry>(() => {
    const e = parseLoc();
    stackRef.current = [e];
    idxRef.current = 0;
    // Normalize the URL on boot so state.i exists for popstate bookkeeping.
    try { window.history.replaceState({ i: 0 }, "", routeToPath(e.route, e.params)); } catch { /* opaque origin */ }
    return e;
  });

  const currentRef = useRef(current); currentRef.current = current;
  const go = useCallback((route: string, params: Record<string, unknown> = {}) => {
    const apply = () => {
    const entry = { route, params };
    const ni = idxRef.current + 1;
    stackRef.current = [...stackRef.current.slice(0, idxRef.current + 1), entry];
    idxRef.current = ni;
    try { window.history.pushState({ i: ni }, "", routeToPath(route, params)); } catch { /* opaque origin */ }
    setCurrent(entry);

    };
    if (!navigationGuarded(apply)) apply();  }, []);

  const replace = useCallback((route: string, params: Record<string, unknown> = {}) => {
    const apply = () => {
    const entry = { route, params };
    const next = [...stackRef.current];
    next[idxRef.current] = entry;
    stackRef.current = next;
    try { window.history.replaceState({ i: idxRef.current }, "", routeToPath(route, params)); } catch { /* opaque origin */ }
    setCurrent(entry);

    };
    if (!navigationGuarded(apply)) apply();  }, []);

  const reset = useCallback((route: string, params: Record<string, unknown> = {}) => {
    const entry = { route, params };
    stackRef.current = [entry];
    idxRef.current = 0;
    try { window.history.replaceState({ i: 0 }, "", routeToPath(route, params)); } catch { /* opaque origin */ }
    setCurrent(entry);
  }, []);

  const back = useCallback(() => {
    try {
      window.history.back();
    } catch {
      if (idxRef.current > 0) { idxRef.current -= 1; setCurrent(stackRef.current[idxRef.current]); }
    }
  }, []);

  // ── Event detail drawer (overlay; rides the URL as ?detail=<id> for share) ──
  const [drawerEvent, setDrawerEvent] = useState<UIEvent | null>(null);
  const openEvent = useCallback((ev: UIEvent) => {
    setDrawerEvent(ev);
    // Reflect the open drawer in the URL (no new history entry) so the link is
    // shareable; closing/navigating clears it. Deep-link rehydration (fetch by
    // id on boot) is a follow-on — the in-memory object covers the live click.
    try {
      const sp = new URLSearchParams(window.location.search);
      if (ev?.id) { sp.set("detail", ev.id); window.history.replaceState(window.history.state, "", window.location.pathname + "?" + sp.toString()); }
    } catch { /* opaque origin */ }
  }, []);
  const closeEvent = useCallback(() => {
    setDrawerEvent(null);
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.has("detail")) { sp.delete("detail"); const q = sp.toString(); window.history.replaceState(window.history.state, "", window.location.pathname + (q ? "?" + q : "")); }
    } catch { /* opaque origin */ }
  }, []);

  // Any real navigation (go/replace/reset change `current`) closes the drawer,
  // matching the prototype. openEvent never changes `current`, so the drawer
  // it just opened survives this effect.
  useEffect(() => { setDrawerEvent(null); }, [current.route, current.params]);

  // Browser back/forward (and deep-link/refresh via the boot replaceState).
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const st = e.state as { i?: number } | null;
      if (restoringPop.current) { restoringPop.current = false; const resume = resumePop.current; resumePop.current = null; resume?.(); return; }
      const approved = approvedPop.current; approvedPop.current = false;
      if (!approved && st && typeof st.i === "number" && stackRef.current[st.i] && st.i !== idxRef.current) {
        const target = st.i, previous = idxRef.current;
        if (navigationGuarded(() => {
          const resume = () => { approvedPop.current = true; window.history.go(target - previous); };
          if (restoringPop.current) resumePop.current = resume; else resume();
        })) {
          restoringPop.current = true; window.history.go(previous - target); return;
        }
      }
      if (st && typeof st.i === "number" && stackRef.current[st.i]) {
        idxRef.current = st.i;
        setCurrent(stackRef.current[st.i]);
      } else {
        // External/deep history change — re-parse the URL as the truth.
        const entry = parseLoc();
        if (!approved && navigationGuarded(() => replace(entry.route, entry.params))) {
          const previous = currentRef.current;
          try { window.history.replaceState({ i: idxRef.current }, "", routeToPath(previous.route, previous.params)); } catch { /* opaque origin */ }
          return;
        }
        const found = stackRef.current.findIndex((s) => s.route === entry.route);
        if (found >= 0) idxRef.current = found;
        setCurrent(entry);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [parseLoc, replace]);

  const value = useMemo<NavApi>(() => ({
    route: current.route,
    params: current.params,
    canBack: idxRef.current > 0,
    canGoBack: idxRef.current > 0,
    prevRoute: idxRef.current > 0 ? stackRef.current[idxRef.current - 1]?.route ?? null : null,
    go, back, replace, reset, openEvent, closeEvent, drawerEvent,
  }), [current, go, back, replace, reset, openEvent, closeEvent, drawerEvent]);

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}
